import { useState, useRef } from "react";
import { PhotoCard } from "../../../workorders/photos/PhotoCard";
import { Button } from "@material-tailwind/react";
import { t } from "i18next";
import SignatureEditForm from "./SignatureEditForm";
import SignatoryInfo from "./SignatoryInfo";
import toast from "react-hot-toast";

interface SignatureCardProps {
  type: "BEFORE" | "AFTER";
  workorderId: string;
  contacts: EasyTech.Contact[];
  canEdit: boolean;
  workorder: any;
  updateWorkorder: (w: any) => void;
  signatures: EasyTech.Photo[];
  setSignatures: (fn: (prev: EasyTech.Photo[]) => EasyTech.Photo[]) => void;
  deleteSignature: (id: string) => Promise<void>;
  getDownloadUrl: (id: string) => string;
}

export default function SignatureCard({
  type,
  workorderId,
  contacts,
  canEdit,
  workorder,
  updateWorkorder,
  signatures,
  setSignatures,
  deleteSignature,
  getDownloadUrl
}: SignatureCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localImage, setLocalImage] = useState<string>("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const signature = signatures.find((sig) => sig.type === type);
  const signatureEditFormRef = useRef<any>(null);

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setIsEditing(false);
    setLocalImage("");
    setPendingFile(null);
  };
  const handleSaved = () => {
    setIsEditing(false);
    setLocalImage("");
    setPendingFile(null);
  };

  return (
    <div className="mb-8 w-full">
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="text-xl font-bold">
          {t(type === "BEFORE" ? "workorder:files:signatureBefore" : "workorder:files:signatureAfter")}
        </h2>
        {canEdit && (
          !isEditing ? (
            <Button
              variant="filled"
              color="blue"
              className="normal-case font-thin"
              onClick={handleEdit}
            >
              {t("Edit")}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="filled"
                color="white"
                className="btn-cancel normal-case font-thin border"
                onClick={handleCancel}
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="filled"
                className="bg-blue-500 normal-case font-thin"
                onClick={() => signatureEditFormRef.current && signatureEditFormRef.current.save()}
              >
                {t("Save")}
              </Button>
            </div>
          )
        )}
      </div>
      <hr className="mb-4" />
      {isEditing ? (
        <>
          <SignatureEditForm
            ref={signatureEditFormRef}
            type={type}
            workorderId={workorderId}
            contacts={contacts}
            canEdit={canEdit}
            workorder={workorder}
            updateWorkorder={updateWorkorder}
            signatures={signatures}
            setSignatures={setSignatures}
            deleteSignature={deleteSignature}
            getDownloadUrl={getDownloadUrl}
            onCancel={handleCancel}
            onSaved={handleSaved}
            localImage={localImage}
            setLocalImage={setLocalImage}
            pendingFile={pendingFile}
            setPendingFile={setPendingFile}
          />
          {/* Affiche la signature existante tant qu'aucun fichier local n'est sélectionné */}
          {!localImage && signature && (
            <PhotoCard
              key={`signature-${signature.id}`}
              workorderId={workorderId}
              photo={signature}
              onDelete={canEdit ? deleteSignature : async () => {
                toast.error("Signature non supprimable");
              }}
              className="w-1/2 h-1/2 rounded-lg aspect-square mb-4"
              type={type}
              getDownloadUrl={getDownloadUrl}
              uploadType="signatures"
              canEdit={canEdit}
            />
          )}
        </>
      ) : (
        <div className="flex flex-col">
          {signature && (
            <>
              <PhotoCard
                key={`signature-${signature.id}`}
                workorderId={workorderId}
                photo={signature}
                onDelete={canEdit ? deleteSignature : async () => {
                  toast.error("Signature non supprimable");
                }}
                className="w-1/2 h-1/2 rounded-lg aspect-square"
                type={type}
                getDownloadUrl={getDownloadUrl}
                uploadType="signatures"
                canEdit={canEdit}
              />
              <SignatoryInfo signature={signature} workorder={workorder} />
            </>
          )}
        </div>
      )}
    </div>
  );
} 